import React from "react";
import { Link } from "react-router-dom";
import Heart from "react-heart";
import CarImageSlider from "./CarImageSlider";
import { toTitleCase } from "../helper/formatCarName";

/**
 * Premium Reusable CarCard Component
 * Retains exact layout and border colors from FilterCarData
 * with tightened white-space and professional UI responsiveness.
 */

const BASE_URL = window.CARLOCA_PUBLIC;

const formatEuroAmount = (amount) => {
  if (amount == null) return "0";
  const parts = amount.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

const formatNumber = (num) => {
  if (!num) return "0";
  if (num >= 10000000) return (num / 10000000).toFixed(2) + "M";
  if (num >= 100000) return (num / 100000).toFixed(2) + "L";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
};

const convertCcToLiters = (cc) => {
  if (cc > 0) return (cc / 1000).toFixed(1);
  return 0;
};

const CarCard = ({
  carData,
  linkTo,
  onClick,
  topLeftContent,
  topRightContent,
  extraBodyContent,
  hideFooter = false,
  footerContent,
}) => {
  if (!carData) return null;

  const {
    id,
    slug,
    image,
    gallery,
    year,
    make,
    model,
    model_varient,
    min_price,
    max_price,
    offer_price,
    transmission_type,
    fuel_type,
    current_mileage,
    engine_capacity,
    owner,
  } = carData;

  const isDealer = owner?.role === "dealer";
  const cardLink = linkTo || `/cardetails?car=${slug}`;

  const handleCardClick = (e) => {
    if (onClick) {
      if (!e.target.closest(".car-card-action, a, button, input, .heart-icon, .swiper-pagination")) {
        onClick(e);
      }
    }
  };

  const cardInner = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#ffffff",
        borderRadius: "10px",
        overflow: "hidden",
        border: "1px solid #eef0f2",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 10px 25px rgba(0, 136, 156, 0.15)";
        e.currentTarget.style.borderColor = "rgba(0, 136, 156, 0.25)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.05)";
        e.currentTarget.style.borderColor = "#eef0f2";
      }}
    >
      {/* 
        ==================================================
        IMAGE CONTAINER (Fixed height matched to FilterCarData)
        ==================================================
      */}
      <div
        className="car-result-img w-100"
        style={{
          display: "flex",
          aspectRatio: "4/3",
        }}
      >
        {/* Main Image (100%) */}
        <div
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#111",
          }}
        >
          <CarImageSlider car={carData} />

          {/* Action Slots */}
          {topLeftContent && (
            <div
              className="car-card-action"
              style={{ position: "absolute", top: "8px", left: "8px", zIndex: 4 }}
            >
              {topLeftContent}
            </div>
          )}

          {/* Year Badge inside Main Image */}
          {year && (
            <div
              className="position-absolute"
              style={{
                bottom: "8px",
                left: "8px",
                backgroundColor: "rgba(0,136,156,0.85)",
                color: "#ffffff",
                fontSize: "11px",
                fontWeight: 600,
                padding: "3px 8px",
                borderRadius: "6px",
                zIndex: 4,
              }}
            >
              {year}
            </div>
          )}

          {/* Gallery Count Badge */}
          {gallery && gallery.length > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: "8px",
                right: "8px",
                background: "rgba(0, 0, 0, 0.75)",
                color: "#fff",
                padding: "4px 10px",
                borderRadius: "12px",
                fontSize: "11px",
                fontWeight: "600",
                zIndex: 4,
                backdropFilter: "blur(4px)",
              }}
            >
              <i className="fa-solid fa-camera me-1"></i>
              {gallery.length} Photos
            </div>
          )}
        </div>

        {/* Top Right Action across full card (like Edit button) */}
        {topRightContent && (
          <div
            className="car-card-action"
            style={{ position: "absolute", top: "8px", right: "8px", zIndex: 3 }}
          >
            {topRightContent}
          </div>
        )}
      </div>

      {/* --- Card Body --- */}
      <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", flexGrow: 1 }}>
        {/* Location */}
        {owner?.city && (
          <p
            style={{
              fontSize: "10px",
              color: "#6b7280",
              margin: "0 0 4px 0",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              gap: "3px",
            }}
          >
            <i className="fa fa-location-dot" style={{ color: "#00889c", fontSize: "10px" }}></i>
            {owner?.city}
          </p>
        )}
        <h6
          style={{
            margin: "0 0 4px 0",
            fontSize: "clamp(13px, 1.2vw, 15px)",
            fontWeight: 800,
            color: "#111827",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textTransform: "capitalize",
          }}
        >
          {toTitleCase(make || "")} {toTitleCase(model || "")}
        </h6>

        {model_varient && (
          <p
            style={{
              fontSize: "11px",
              color: "#6b7280",
              margin: "0 0 6px 0",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {model_varient}
          </p>
        )}

        {/* Price */}
        <div style={{ marginBottom: "6px" }}>
          {!isDealer ? (
            <div style={{ color: "#00889c", fontSize: "clamp(13px, 1.2vw, 15px)", fontWeight: 800, lineHeight: 1.2 }}>
              ₹{formatEuroAmount(min_price)}{" "}
              <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>– ₹{formatEuroAmount(max_price)}</span>
            </div>
          ) : (
            <div style={{ color: "#00889c", fontSize: "clamp(13px, 1.2vw, 15px)", fontWeight: 800, lineHeight: 1.2 }}>
              ₹{formatEuroAmount(offer_price)}
            </div>
          )}
        </div>

        {/* --- Specs Grid (Compact & Professional) --- */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "4px",
            marginBottom: extraBodyContent ? "10px" : "0",
          }}
        >
          {transmission_type && (
            <div style={{ display: "inline-flex", alignItems: "center", background: "#f9fafb", border: "1px solid #e5e7eb", padding: "4px 6px", borderRadius: "4px", fontSize: "10px", color: "#374151", fontWeight: 500 }}>
              <i className="fa-solid fa-gears me-1" style={{ color: "#00889c" }}></i>
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{transmission_type}</span>
            </div>
          )}
          {current_mileage && (
            <div style={{ display: "inline-flex", alignItems: "center", background: "#f9fafb", border: "1px solid #e5e7eb", padding: "4px 6px", borderRadius: "4px", fontSize: "10px", color: "#374151", fontWeight: 500 }}>
              <i className="fa-solid fa-road me-1" style={{ color: "#00889c" }}></i>
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{formatNumber(current_mileage)}</span>
            </div>
          )}
          {fuel_type && (
            <div style={{ display: "inline-flex", alignItems: "center", background: "#f9fafb", border: "1px solid #e5e7eb", padding: "4px 6px", borderRadius: "4px", fontSize: "10px", color: "#374151", fontWeight: 500 }}>
              <i className="fa-solid fa-gas-pump me-1" style={{ color: "#00889c" }}></i>
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fuel_type}</span>
            </div>
          )}
          {engine_capacity && (
            <div style={{ display: "inline-flex", alignItems: "center", background: "#f9fafb", border: "1px solid #e5e7eb", padding: "4px 6px", borderRadius: "4px", fontSize: "10px", color: "#374151", fontWeight: 500 }}>
              <i className="fa-solid fa-gauge-high me-1" style={{ color: "#00889c" }}></i>
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{convertCcToLiters(engine_capacity)}L</span>
            </div>
          )}
        </div>

        {/* Interactive Extra Body Content */}
        {extraBodyContent && (
          <div style={{ marginTop: "0px" }}>
            {extraBodyContent}
          </div>
        )}
      </div>

      {/* --- Footer Section --- */}
      {!hideFooter && (
        <div
          style={{
            padding: "6px 10px",
            borderTop: "1px solid #f3f4f6",
            background: "#f9fafb",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {footerContent ? (
            footerContent
          ) : isDealer ? (
            <Link
              to="/dealer/store"
              state={{ owner_id: owner?.id }}
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                width: "100%",
              }}
            >
              <img
                src={
                  owner?.profile
                    ? `${window.CARLOCA_UPLOADS}/users/${owner?.profile}`
                    : `https://ui-avatars.com/api/?name=${owner?.dealerName || owner?.first_name || "D"
                    }+${owner?.last_name || ""}&background=random`
                }
                alt="Dealer"
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1px solid #e5e7eb",
                  marginRight: "6px",
                }}
              />
              <span
                style={{
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: "11px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textTransform: "capitalize",
                }}
              >
                {owner?.dealerName || owner?.first_name || "Dealer"}
              </span>
              <div
                style={{
                  marginLeft: "auto",
                  color: "#00889c"
                }}
              >
                <i className="fa-solid fa-chevron-right" style={{ fontSize: "10px" }}></i>
              </div>
            </Link>
          ) : (
            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
              <span style={{ color: "#374151", fontWeight: 600, fontSize: "11px" }}>Private Seller</span>
              {owner?.city && (
                <span
                  style={{
                    marginLeft: "auto",
                    color: "#6b7280",
                    fontSize: "10px",
                    textTransform: "capitalize",
                    fontWeight: 500,
                  }}
                >
                  <i className="fa fa-location-dot me-1" style={{ color: "#00889c" }}></i>
                  {owner?.city}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (onClick) {
    return (
      <div
        className="car-result-card h-100 position-relative"
        onClick={handleCardClick}
        style={{ cursor: "pointer", display: "block" }}
      >
        {cardInner}
      </div>
    );
  }

  return (
    <Link
      to={cardLink}
      className="car-result-card h-100 position-relative text-decoration-none"
      style={{ cursor: "pointer", display: "block" }}
    >
      {cardInner}
    </Link>
  );
};

export default CarCard;
